import type { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import { RefreshClient } from "@citrine/client";

import type { TableData, TrainingData } from "../data-source";
import { getDataIdentifier, isCSVData, isTableData } from "../data-source";
import type { IFileLink } from "../file";
import type {
  IPredictorConfig,
  TemplateSummary,
  TemplateType,
} from "../predictor";

import type {
  FormulationRelationsResponse,
  IDescriptor,
  IDescriptorInference,
  IGetDescriptorsFromDataSource,
} from "./descriptor";
import { transformDescriptor } from "./descriptor";

interface IGetPredictorResponses {
  responses: IDescriptor[];
}

export function getPredictorResponseKey(
  predictorConfig?: IPredictorConfig,
  inputs?: IDescriptor[],
) {
  return [
    "get-descriptors-predictor-responses",
    JSON.stringify(predictorConfig),
    ...(inputs?.map(({ descriptor_key }) => descriptor_key) ?? []).sort(),
  ];
}

export class DescriptorClient extends RefreshClient {
  endpoint: string;
  projectId: string;

  protected constructor(projectId: string) {
    super();
    this.endpoint = `/api/v1/projects/${projectId}/material-descriptors`;
    this.projectId = projectId;
  }

  static getInstance(projectId: string) {
    return new this(projectId);
  }

  async getPredictorResponses(
    predictorConfig: IPredictorConfig,
    inputs: IDescriptor[],
  ): Promise<IDescriptor[]> {
    const result = await this.request<IGetPredictorResponses>({
      method: "POST",
      url: `${this.endpoint}/predictor-responses`,
      data: {
        predictor: predictorConfig,
        inputs,
      },
    });
    return result.responses;
  }

  /**
   * Returns possible outputs for a given predictor and inputs.
   * Useful if you need to know what sorts of features could result from certain featurizers
   * given a specific set of input descriptors.
   */
  public useGetPredictorResponses(
    predictorConfig?: IPredictorConfig,
    inputs?: IDescriptor[],
    options?: Omit<UseQueryOptions<IDescriptor[]>, "queryKey" | "queryFn">,
  ) {
    return useQuery({
      enabled: Boolean(predictorConfig) && Boolean(inputs),
      staleTime: Infinity,
      ...options,
      queryKey: getPredictorResponseKey(predictorConfig, inputs),
      queryFn: () => this.getPredictorResponses(predictorConfig!, inputs!),
    });
  }

  async getDescriptorsFromDataSource(
    data_source: TrainingData,
  ): Promise<IDescriptor[]> {
    const result = await this.request<IGetDescriptorsFromDataSource>({
      method: "POST",
      url: `${this.endpoint}/from-data-source`,
      data: {
        data_source,
      },
    });
    result.descriptors.forEach(transformDescriptor);
    return result.descriptors;
  }

  async getDescriptorsFromDataSourceList(dataSources: TrainingData[]) {
    const allDatasourceDescriptors = await Promise.all(
      dataSources.flatMap((ds) => this.getDescriptorsFromDataSource(ds)),
    );
    return allDatasourceDescriptors.flatMap((d) => d);
  }

  public useGetDescriptorsFromDataSourceList(
    dataSources: TrainingData[],
    options?: Omit<UseQueryOptions<IDescriptor[]>, "queryKey" | "queryFn">,
  ) {
    const queryKey = [
      "get-descriptors-from-data-source",
      ...dataSources.flatMap(getDataIdentifier),
    ];
    return useQuery({
      ...options,
      queryKey,
      queryFn: () => this.getDescriptorsFromDataSourceList(dataSources),
    });
  }

  public useGetDescriptorsFromDataSource(
    dataSource?: TrainingData,
    options?: Omit<UseQueryOptions<IDescriptor[]>, "queryKey" | "queryFn">,
  ) {
    const queryKey = ["get-descriptors-from-data-source"];

    if (isCSVData(dataSource)) {
      queryKey.push(dataSource.file_link.url);
    }

    if (isTableData(dataSource)) {
      queryKey.push(dataSource.table_id);
      queryKey.push(dataSource.table_version.toString());
    }

    return useQuery({
      enabled: Boolean(dataSource),
      staleTime: Infinity,
      ...options,
      queryKey,
      queryFn: () => this.getDescriptorsFromDataSource(dataSource!),
    });
  }

  /**
   * Used for mapping hierarchical candidates to materials. Returns the
   * templates paired with the descriptor keys derived from them.
   * Only GEM-table and snapshot data sources carry this metadata.
   */
  async getTemplatesFromDataSource(
    data_source: TrainingData,
    template_type: TemplateType,
  ): Promise<TemplateSummary[]> {
    const result = await this.request<{
      data_source_type: string;
      templates: TemplateSummary[];
    }>({
      method: "POST",
      url: `${this.endpoint}/templates`,
      data: { data_source, template_type },
    });
    return result.templates;
  }

  public useGetTemplatesFromDataSource(
    dataSource: TrainingData | undefined,
    templateType: TemplateType,
    options?: Omit<UseQueryOptions<TemplateSummary[]>, "queryKey" | "queryFn">,
  ): UseQueryResult<TemplateSummary[]> {
    const queryKey = [
      `get-${templateType}-templates-from-data-source`,
      ...(dataSource ? [getDataIdentifier(dataSource)] : []),
    ];

    return useQuery({
      enabled: Boolean(dataSource),
      staleTime: Infinity,
      ...options,
      queryKey,
      queryFn: () => this.getTemplatesFromDataSource(dataSource!, templateType),
    });
  }

  async getDescriptorTypeInferenceFromCsv(file: IFileLink) {
    // This function extracts the file id from the versioned url
    // Do NOT use the file-id in the IFile object
    const payload = await this.request<IDescriptorInference>({
      method: "POST",
      url: `${this.endpoint}/infer-from-csv-file`,
      data: {
        file,
      },
    });

    payload.guesses.forEach((guess) => {
      guess.scored_descriptors.forEach((sd) => {
        transformDescriptor(sd.descriptor);
      });
    });

    return payload;
  }
  /**
   * Hook to fetch descriptor inferences. It accepts a CSV file and returns
   * and object that guesses the descriptor types with default values
   * @param  {IFile|undefined} file
   * @param  {QueryOptions<IDescriptorInference>} options?
   */
  public useGetDescriptorTypeInferenceFromCsv(
    file: IFileLink | undefined,
    options?: Omit<
      UseQueryOptions<IDescriptorInference>,
      "queryKey" | "queryFn"
    >,
  ): UseQueryResult<IDescriptorInference> {
    return useQuery({
      enabled: Boolean(file?.url),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      ...options,
      queryKey: ["infer-descriptor-types-from-csv", `${file?.url}`],
      queryFn: () => this.getDescriptorTypeInferenceFromCsv(file!),
    });
  }

  /**
   * This endpoint retrieves all the descriptor relations for a formulation,
   * given a GEM TABLE data source.
   * @param table
   */
  public async getDescriptorRelations(table: TableData) {
    const result = await this.request<FormulationRelationsResponse>({
      url: `/api/v1/projects/${this.projectId}/data-sources/formulation-data/${table.table_id}/${table.table_version}`,
      method: "GET",
    });
    return result;
  }

  public getDescriptorRelationsFromList(tables: TableData[]) {
    return Promise.all(
      tables.map((table) => this.getDescriptorRelations(table)),
    );
  }

  public useGetDescriptorRelationsFromList(
    tables: TableData[],
    options?: Omit<
      UseQueryOptions<FormulationRelationsResponse[]>,
      "queryKey" | "queryFn"
    >,
  ) {
    const cacheKey = [
      "get-descriptors-relations-from-list",
      ...tables.flatMap((dataSource) => [
        dataSource.table_id,
        dataSource.table_version.toString(),
      ]),
    ];

    return useQuery({
      ...options,
      queryKey: cacheKey,
      queryFn: () => this.getDescriptorRelationsFromList(tables),
    });
  }

  /**
   * Hook to get descriptor relations for a fomulation.
   */
  public useGetDescriptorRelations(
    table?: TableData,
    options?: Omit<
      UseQueryOptions<FormulationRelationsResponse>,
      "queryKey" | "queryFn"
    >,
  ) {
    return useQuery({
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      ...options,
      enabled: Boolean(table) && (options?.enabled ?? true),
      queryKey: [
        `/api/v1/projects/${this.projectId}/data-sources/formulation-data/${table?.table_id}/${table?.table_version}`,
      ],
      queryFn: () => this.getDescriptorRelations(table!),
    });
  }

  protected toModel(payload: any): IDescriptor {
    transformDescriptor(payload);
    return payload;
  }
}
